/**
 * Google sign-in. With `VITE_GOOGLE_CLIENT_ID` set, Google Identity Services
 * issues an id_token that `POST /v1/auth/google` verifies. Without it (this
 * box, no Google project yet) the button falls back to the dev backend's
 * `POST /dev/login`, which mints the same kind of session. PLACEHOLDER until
 * a client id exists.
 */
import { dev, post } from "../api/client";
import type { User } from "../api/types";

export const GOOGLE_CLIENT_ID: string | undefined = import.meta.env.VITE_GOOGLE_CLIENT_ID;
export const googleConfigured = !!GOOGLE_CLIENT_ID;

declare global {
  interface Window { google?: { accounts: { id: { initialize: (o: Record<string, unknown>) => void; prompt: () => void; renderButton: (el: HTMLElement, o: Record<string, unknown>) => void } } } }
}

let loading: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (window.google) return Promise.resolve();
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error("could not load Google sign-in"));
      document.head.appendChild(s);
    });
  }
  return loading;
}

/** Real path: prompt Google, exchange the id_token for a session. */
export async function signInWithGoogle(opts?: {
  turnstileToken?: (() => string | undefined) | undefined;
  inviteToken?: string | undefined;
  confirmEmailMismatch?: boolean | undefined;
  handle?: string | undefined;
}): Promise<{ user: User; created: boolean; claimed?: { team_id: string; video_id: string | null } | null; invite_claimed?: { team_id: string; role?: string; video_id: string | null } | null }> {
  await loadGis();
  const idToken = await new Promise<string>((resolve, reject) => {
    window.google!.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: (r: { credential?: string }) => (r.credential ? resolve(r.credential) : reject(new Error("no credential"))),
      ux_mode: "popup",
    });
    window.google!.accounts.id.prompt();
  });
  return post("/v1/auth/google", {
    id_token: idToken,
    turnstile_token: opts?.turnstileToken?.(),
    ...(opts?.inviteToken ? { invite_token: opts.inviteToken } : {}),
    ...(opts?.confirmEmailMismatch ? { confirm_email_mismatch: true } : {}),
    ...(opts?.handle ? { handle: opts.handle } : {}),
  });
}

/** Placeholder path: the dev backend mints a session for `email` on `plan`. */
export async function signInDev(email: string, plan: "free" | "basic" | "pro", name?: string, handle?: string): Promise<void> {
  if (!dev.enabled) throw new Error("Google sign-in is not configured and the dev backend is not enabled");
  await dev.login(email, plan, name, handle);
}
